import { action, computed, makeObservable } from 'mobx';

import {
  BUSINESS_HOURS_WEEKDAYS,
  BusinessHoursWeekday,
  createDefaultBusinessHoursDay,
  createDefaultBusinessHoursSettings,
  IBusinessHoursDay,
  IBusinessHoursSettings,
  BusinessHoursSerializationOptions,
  isBusinessHoursWorkingDayValid,
  DEFAULT_WORKING_DAY_END,
  DEFAULT_WORKING_DAY_START,
  validateBusinessHoursAutoReply,
  validateBusinessHoursTime,
  validateBusinessHoursTimeRange
} from 'shared/entities/projectSettings';
import { FieldModel } from 'shared/models/form';

export type BusinessHoursDayModel = {
  weekday: BusinessHoursWeekday;
  dayOff: FieldModel<boolean>;
  start: FieldModel<string | null>;
  end: FieldModel<string | null>;
};

export default class BusinessHoursSettingsModel {
  readonly enabled: FieldModel<boolean>;
  readonly autoReplyEnabled: FieldModel<boolean>;
  readonly autoReplyText: FieldModel<string>;
  readonly days: BusinessHoursDayModel[];

  static fromDefaultParams(): BusinessHoursSettingsModel {
    return new BusinessHoursSettingsModel(createDefaultBusinessHoursSettings());
  }

  constructor({ enabled, schedule, autoReply }: IBusinessHoursSettings) {
    this.enabled = new FieldModel(enabled);
    this.autoReplyEnabled = new FieldModel(autoReply.enabled);
    this.autoReplyText = new FieldModel(autoReply.text, [
      (value) =>
        validateBusinessHoursAutoReply(value, this.autoReplyEnabled.value)
    ]);
    this.days = BUSINESS_HOURS_WEEKDAYS.map((weekday) => {
      const day =
        schedule.find((item) => item.weekday === weekday) ??
        createDefaultBusinessHoursDay(weekday);

      return this._createDayModel(day);
    });

    makeObservable(this, {
      touched: computed,
      isValid: computed,
      canSave: computed,
      scheduleTouched: computed,
      autoReplyCanSave: computed,
      changeEnabled: action.bound,
      changeAutoReplyEnabled: action.bound,
      changeAutoReplyText: action.bound,
      changeDayOff: action.bound,
      changeDayStart: action.bound,
      changeDayEnd: action.bound,
      validate: action.bound,
      validateSchedule: action.bound,
      reset: action.bound,
      resetSchedule: action.bound,
      commitSchedule: action.bound,
      commitAutoReply: action.bound
    });
  }

  get touched(): boolean {
    return (
      this.enabled.touched ||
      this.autoReplyEnabled.touched ||
      this.autoReplyText.touched ||
      this.days.some(
        (day) => day.dayOff.touched || day.start.touched || day.end.touched
      )
    );
  }

  get scheduleTouched(): boolean {
    return (
      this.enabled.touched ||
      this.days.some(
        (day) => day.dayOff.touched || day.start.touched || day.end.touched
      )
    );
  }

  get isValid(): boolean {
    return (
      this.scheduleIsValid &&
      !validateBusinessHoursAutoReply(
        this.autoReplyText.value,
        this.autoReplyEnabled.value
      )
    );
  }

  get canSave(): boolean {
    return this.scheduleTouched && this.scheduleIsValid;
  }

  get autoReplyCanSave(): boolean {
    return (
      (this.autoReplyEnabled.touched || this.autoReplyText.touched) &&
      !validateBusinessHoursAutoReply(
        this.autoReplyText.value,
        this.autoReplyEnabled.value
      )
    );
  }

  private get scheduleIsValid(): boolean {
    return this.days.every(({ dayOff, start, end }) =>
      isBusinessHoursWorkingDayValid(dayOff.value, start.value, end.value)
    );
  }

  changeEnabled(value: boolean): void {
    if (!value) {
      this.reset();
    }

    this.enabled.changeValue(value);
  }

  changeAutoReplyEnabled(value: boolean): void {
    if (!value && this.autoReplyText.touched) {
      const error = this.autoReplyText.validate();

      if (error) {
        this.autoReplyText.reset();
      }
    } else {
      this.autoReplyText.reset();
    }

    this.autoReplyEnabled.changeValue(value);

    if (value && !this.autoReplyText.touched) {
      this.autoReplyText.resetError();
      return;
    }

    this._validateAutoReplyText();
  }

  changeAutoReplyText(value: string): void {
    this.autoReplyText.changeValue(value);
    this._validateAutoReplyText();
  }

  changeDayOff(weekday: BusinessHoursWeekday, value: boolean): void {
    const day = this._getDay(weekday);

    if (!day) {
      return;
    }

    if (!value && !day.start.value && !day.end.value) {
      day.start.changeValue(DEFAULT_WORKING_DAY_START);
      day.end.changeValue(DEFAULT_WORKING_DAY_END);
    }

    day.dayOff.changeValue(value);
    this._validateDay(day);
  }

  changeDayStart(weekday: BusinessHoursWeekday, value: string): void {
    const day = this._getDay(weekday);

    if (!day) {
      return;
    }

    day.start.changeValue(value || null);
    this._validateDay(day);
  }

  changeDayEnd(weekday: BusinessHoursWeekday, value: string): void {
    const day = this._getDay(weekday);

    if (!day) {
      return;
    }

    day.end.changeValue(value || null);
    this._validateDay(day);
  }

  validate(): boolean {
    const autoReplyValid = this._validateAutoReplyText();
    const daysValid = this.validateSchedule();

    return autoReplyValid && daysValid;
  }

  validateSchedule(): boolean {
    return this.days.map((day) => this._validateDay(day)).every(Boolean);
  }

  reset(): void {
    this.enabled.reset();
    this.autoReplyEnabled.reset();
    this.autoReplyText.reset();
    this.days.forEach((day) => {
      day.dayOff.reset();
      day.start.reset();
      day.end.reset();
    });
  }

  resetSchedule(): void {
    this.enabled.reset();
    this.days.forEach((day) => {
      day.dayOff.reset();
      day.start.reset();
      day.end.reset();
    });
  }

  commitSchedule(): void {
    this.enabled.resetTouched();
    this.days.forEach((day) => {
      day.dayOff.resetTouched();
      day.start.resetTouched();
      day.end.resetTouched();
    });
  }

  commitAutoReply(): void {
    this.autoReplyEnabled.resetTouched();
    this.autoReplyText.resetTouched();
  }

  toJSON({
    withSavedSchedule = false,
    withSavedAutoReply = false
  }: BusinessHoursSerializationOptions = {}) {
    return {
      enabled: this._getFieldValue(this.enabled, withSavedSchedule),
      schedule: this.days.map((day) =>
        this._serializeDay(day, withSavedSchedule)
      ),
      auto_reply: {
        enabled: this._getFieldValue(this.autoReplyEnabled, withSavedAutoReply),
        text: this._getFieldValue(this.autoReplyText, withSavedAutoReply)
      }
    };
  }

  private _serializeDay(
    day: BusinessHoursDayModel,
    withSavedSchedule: boolean
  ) {
    const dayOff = this._getFieldValue(day.dayOff, withSavedSchedule);

    return {
      weekday: day.weekday,
      day_off: dayOff,
      start: dayOff ? null : this._getFieldValue(day.start, withSavedSchedule),
      end: dayOff ? null : this._getFieldValue(day.end, withSavedSchedule)
    };
  }

  private _getFieldValue<T>(
    field: FieldModel<T>,
    fromInitialValue: boolean
  ): T {
    return fromInitialValue ? field.initialValue : field.value;
  }

  private _createDayModel(day: IBusinessHoursDay): BusinessHoursDayModel {
    const dayOff = new FieldModel(day.dayOff);
    const start = new FieldModel(day.start, [
      (value) => validateBusinessHoursTime(value, dayOff.value)
    ]);
    const end = new FieldModel(day.end, [
      (value) => validateBusinessHoursTime(value, dayOff.value),
      (value) =>
        validateBusinessHoursTimeRange(start.value, value, dayOff.value)
    ]);

    return {
      weekday: day.weekday,
      dayOff,
      start,
      end
    };
  }

  private _getDay(
    weekday: BusinessHoursWeekday
  ): BusinessHoursDayModel | undefined {
    return this.days.find((item) => item.weekday === weekday);
  }

  private _validateAutoReplyText(): boolean {
    const error = validateBusinessHoursAutoReply(
      this.autoReplyText.value,
      this.autoReplyEnabled.value
    );

    this.autoReplyText.setError(error);

    return !error;
  }

  private _validateDay(day: BusinessHoursDayModel): boolean {
    day.start.validate();
    day.end.validate();

    return !day.start.error && !day.end.error;
  }
}
